import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/app/providers/user';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { Button, GoogleMark, Input, ValoiVLockup } from '@/shared/ui';

/*
 * Iteration 2.0 has no sign-in screen, so this is inferred from the rest of the
 * system: the cream surface, the display serif for the promise, and the same
 * brand principles the Figma branding block states — approachable, illuminating,
 * human.
 *
 * The dark-green marketing panel and the fake "Rendering" progress card are
 * gone. The old panel sold the product to someone who has already decided to
 * sign in, and animated a job that does not exist.
 */
export default function LoginPage() {
  const { loginWithGoogle, mockLogin, sessionExpired } = useCurrentUser();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');

  const handleGoogle = async () => {
    setError(null);
    setBusy(true);
    try {
      await loginWithGoogle();
    } catch {
      setBusy(false);
      setError('Could not start Google sign-in. Please try again.');
    }
  };

  const handleMock = (event: FormEvent) => {
    event.preventDefault();
    if (!mockLogin) return;
    if (!displayName.trim() || !email.trim()) {
      setError('Please provide a display name and email.');
      return;
    }
    mockLogin({ displayName: displayName.trim(), email: email.trim() });
    navigate('/', { replace: true });
  };

  return (
    <div className="auth">
      <div className="auth__card">
        {/* A link, not a picture. The mark is the one thing on this screen that
            looks like a way back, and it was inert -- someone who arrived here by
            mistake had no exit but the browser's back button. `/` is the public
            landing page, so it works whether or not they have an account. */}
        <Link to="/" className="auth__brand" aria-label="Valoi — home">
          <ValoiVLockup title="Valoi" className="auth__logo" />
        </Link>

        <h1 className="auth__title">What are we bringing to life today?</h1>
        <p className="auth__lead">
          Sign in to turn any document into a video you can watch, annotate, and keep.
        </p>

        {sessionExpired ? (
          <div className="auth__notice" role="status">
            Your session expired. Please sign in again to continue.
          </div>
        ) : null}

        <Button
          variant="secondary"
          size="lg"
          block
          loading={busy}
          onClick={handleGoogle}
          // No mark while loading: Button puts its spinner in this same slot.
          iconLeft={busy ? undefined : <GoogleMark />}
        >
          {busy ? 'Redirecting to Google…' : 'Sign in with Google'}
        </Button>

        {error ? <ErrorBanner message={error} onDismiss={() => setError(null)} /> : null}

        <p className="auth__secure">Secured with Google OAuth · SSO enabled</p>

        {mockLogin ? (
          <form className="auth__mock" onSubmit={handleMock}>
            <div className="auth__divider">Dev-only mock login</div>
            <Input
              label="Display name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Valoi User"
            />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="user@valoi.ai"
            />
            <Button type="submit" block>
              Mock sign in
            </Button>
          </form>
        ) : null}
      </div>

      <p className="auth__terms">
        By continuing you agree to Valoi’s <a href="#">Terms</a> and <a href="#">Privacy Policy</a>.
      </p>
    </div>
  );
}
